// Catalog.ID Client — Confirming a device mode change
// ============================================================================
// device-setup-restructure §15 A, decided 2026-09-18. The screen half of
// "present every factor you remove or change": after the member has chosen the
// new mode — and typed the NEW PIN, or the live code an Authenticator mode
// needs — this asks for the factors the change TAKES AWAY and drives
// `PUT /api/device/pin` until the node accepts.
//
// Three things worth knowing before reading it:
//
//  * The node's `factors_required` answer is authoritative. `factorsToPresent`
//    is only the opening guess, so the member is asked once rather than refused
//    and then asked; every refusal repaints from what the node named.
//  * The step-up challenge is SINGLE USE, so a wrong factor burns it. Every
//    attempt mints a fresh one — which is why `signStepUp` is called inside the
//    loop and not once at the top.
//  * At most ONE code is needed per change. When the mode step already took a
//    live code, the confirm step must not ask for a second: the node's `code`
//    field carries both meanings (§15).
//
// The inputs themselves are `components/factor-inputs.ts`, shared with the
// unlock screen; the decisions are `auth/device-mode-change.ts`.

import { createElement } from '@catalog-shell';
import { createFactorInputs } from './factor-inputs';
import {
    classifyModeChangeFailure, describeModeChangeError, factorsStillToCollect,
    factorsToPresent, routesOutOfTheModal,
    type DeviceModeNow, type ModeChangeOutcome,
} from '../auth/device-mode-change';
import type { UnlockFactor } from '../auth/device-unlock';
import type { ChangePinRequest } from '../api/types';

/** The write the member has chosen — everything but the two fields this module
 *  supplies itself. */
export type ModeChangeRequest = Omit<ChangePinRequest, 'currentPin' | 'challengeSignature'>;

export interface ConfirmModeChangeOptions {
    /** Where the confirm step paints. Emptied of nothing — it appends. */
    host: HTMLElement;
    /** The device row as it stands TODAY. */
    current: DeviceModeNow;
    /** The change itself. Passed through to the node untouched. */
    request: ModeChangeRequest;
    /** Mint a FRESH single-use `device-manage` challenge. One per attempt. */
    signStepUp(): Promise<string>;
    /** `PUT /api/device/pin`. */
    changePin(req: ChangePinRequest): Promise<void>;
}

const sameFactors = (a: readonly UnlockFactor[], b: readonly UnlockFactor[]): boolean =>
    a.length === b.length && a.every((f, i) => f === b[i]);

/**
 * Collect whatever the change has to present, send it, and resolve when the
 * node accepts.
 *
 * Rejects with the sentence to show the member when re-typing cannot help — a
 * lockout, a vanished row, a dead session, or a refusal that is not about
 * factors at all. The caller decides what to do with that (the Devices pane
 * shows it; a lockout's sentence says the way back is to sign out and use the
 * supersecret).
 */
export async function confirmAndChangePin(options: ConfirmModeChangeOptions): Promise<void> {
    const { host, current } = options;
    // `code` is pulled OUT of the request and re-added per attempt: once a code
    // has been refused this module clears it, and a spread of the original
    // request would quietly put the dead one back on the wire.
    const { code: chosenCode, ...request } = options.request;

    let currentPin: string | undefined;
    let code = chosenCode;
    let finish: { resolve: () => void; reject: (err: Error) => void } | null = null;

    let needed = factorsToPresent(current, {
        accessMode: request.accessMode,
        totpRequired: request.totpRequired ?? false,
        pin: request.pin,
        pinLength: request.pinLength,
    });

    async function attempt(): Promise<ModeChangeOutcome | null> {
        try {
            await options.changePin({
                ...request,
                ...(code === undefined ? {} : { code }),
                ...(currentPin === undefined ? {} : { currentPin }),
                challengeSignature: await options.signStepUp(),
            });
            return null;
        } catch (err) {
            return classifyModeChangeFailure(err);
        }
    }

    // Nothing to collect: send it as it stands. Most changes end here — adding
    // a PIN, or an Authenticator mode whose code the mode step already took.
    if (factorsStillToCollect(needed, { currentPin, code }).length === 0) {
        const outcome = await attempt();
        if (outcome === null) return;
        if (outcome.status !== 'factors-required') throw new Error(describeModeChangeError(outcome));
        needed = outcome.factors;
    }

    // Something is missing. Ask for it, and keep asking until the node is
    // satisfied or the answer stops being about factors.
    const section = createElement('div', { className: 'cid-wizard-step' });
    section.appendChild(createElement('h4', {
        className: 'cid-wizard-heading',
        textContent: 'Confirm this change',
    }));
    const prompt = createElement('p', { className: 'cid-wizard-text' });
    section.appendChild(prompt);
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    host.appendChild(section);

    let painted = paintable(needed, currentPin, code);
    const inputs = createFactorInputs({
        factors: painted,
        pinLength: pinLengthOf(current),
        pinLengthChooser: true,
        onSubmit: submit,
    });
    section.appendChild(inputs.element);
    section.appendChild(errorEl);
    prompt.textContent = promptFor(painted);

    return new Promise<void>((resolve, reject) => {
        finish = { resolve, reject };
    });

    // ── the loop ──────────────────────────────────────────────────────────

    async function submit(values: { pin?: string; code?: string }): Promise<void> {
        if (painted.includes('pin')) currentPin = values.pin;
        if (painted.includes('totp')) code = values.code;

        errorEl.textContent = '';
        errorEl.className = 'cid-pane-error';

        const outcome = await attempt();
        if (outcome === null) {
            finish?.resolve();
            return;
        }
        if (routesOutOfTheModal(outcome)) {
            finish?.reject(new Error(describeModeChangeError(outcome)));
            return;
        }

        // What survives the retry: the node verifies the PIN first and outside
        // the code limiter, so a code refusal proves the PIN was right and a PIN
        // refusal means the code was never reached (§1 item 3). A rate-limited
        // code is unusable for now, so it goes the same way as a wrong one.
        if (outcome.status === 'wrong-pin') currentPin = undefined;
        if (outcome.status === 'wrong-code' || outcome.status === 'rate-limited') code = undefined;
        if (outcome.status === 'factors-required') needed = outcome.factors;

        errorEl.textContent = describeModeChangeError(outcome);
        errorEl.className = 'cid-pane-error cid-pane-error--visible';

        // `keep` first, then the repaint: the repaint renders from the inputs'
        // own state, so clearing has to land before it.
        inputs.keep({ pin: currentPin, code });
        const next = paintable(needed, currentPin, code);
        if (!sameFactors(next, painted)) {
            painted = next;
            inputs.setFactors(painted);
            prompt.textContent = promptFor(painted);
        }
    }

    function paintable(
        want: readonly UnlockFactor[], pin: string | undefined, typed: string | undefined,
    ): UnlockFactor[] {
        const missing = factorsStillToCollect(want, { currentPin: pin, code: typed });
        // Everything the node named is already in hand and it still refused —
        // ask for all of it again rather than paint an empty step the member
        // can never satisfy.
        return missing.length > 0 ? missing : [...want];
    }
}

function pinLengthOf(current: DeviceModeNow): 4 | 6 | 8 {
    return current.pinLength === 6 || current.pinLength === 8 ? current.pinLength : 4;
}

/** What the confirm step asks for, in the member's words. */
function promptFor(factors: readonly UnlockFactor[]): string {
    const pin = factors.includes('pin');
    const code = factors.includes('totp');
    if (pin && code) return 'Enter your current PIN and a code from your authenticator app.';
    if (pin) return 'Enter your current PIN.';
    return 'Enter a code from your authenticator app.';
}
